// /products/web/src/services/client-logs.service.ts
/**
 * Client Logs Service
 * Handles all client-side logging calls to the backend
 */

import { callApi } from "@src/utils/api-client";
import { getCorrelationId } from "@src/utils/correlation-id";
import { type FailureType, getLogLevelForFailureType } from "@src/utils/failure-taxonomy";
import logger, { type LogObject, setLoggerUserRole } from "@src/utils/logger";
import { buildClientLogsConfig } from "./configs";

export interface ClientLogPayload {
  userUuid: string;
  url: string;
  location: string;
  eventType:
    | "click"
    | "error"
    | "userError"
    | "navigation"
    | "webAction"
    | "feature_exposure_rendered";
  message?: string;
  error?: string;
  timestamp?: string;
  elementId?: string;
  userRole?: string;
  bootstrapTransactionID?: string;
  featureKey?: string;
  variant?: string;
  experience?: string;
  route?: string;
  eventTimestamp?: string;
  eventName?: string;
  details?: Record<string, string | number | boolean | null>;
  // ADR-03 failure taxonomy classification (eventType 'error' only)
  errorType?: FailureType;
  dependencyName?: string;
  retryCount?: number;
  // ADR-03 structured logging contract
  correlationId?: string;
  functionName?: string;
}

export type ProviderAvailabilityOutcomeEventName =
  | "provider_availability_first_usable"
  | "provider_availability_all_settled"
  | "provider_availability_fallback"
  | "provider_availability_terminal";

class ClientLogsService {
  /**
   * Returns the active ADR-03 correlation id (shared with `callApi`), minting
   * one on first use.
   */
  static getCorrelationId(): string {
    return getCorrelationId();
  }

  /**
   * The active user role (patient, referrer, patient navigator), set once known.
   * Automatically enriches outgoing logs so downstream consumers can attribute
   * events to a role without every call site passing it explicitly.
   */
  private static userRole: string | undefined;

  /**
   * Set the active user role for subsequent client logs.
   */
  static setUserRole(role: string | undefined): void {
    ClientLogsService.userRole = role;
    // Keep the base logger in sync so direct logger.* calls are also enriched.
    setLoggerUserRole(role);
  }

  /**
   * Internal logger - logs to console via the frontend logger utility
   * This ensures all logging is centralized through this service
   */
  private static logInternal({ level = "info", text, value, error, location }: LogObject): void {
    const logMethod = logger[level as keyof typeof logger];
    if (logMethod) {
      logMethod({
        text,
        value,
        error,
        location,
      });
    }
  }

  /**
   * Send a log to the backend
   * Non-blocking async operation that won't affect user experience
   * @param logObj - Optional LogObject to trigger internal logging
   */
  static async sendLog(payload: ClientLogPayload, logObj?: LogObject): Promise<void> {
    if (logObj) {
      ClientLogsService.logInternal(logObj);
    }

    // Only send to backend if running in browser context
    if (typeof window === "undefined") {
      return;
    }

    // Enrich with the active user role and session correlation id, without
    // overriding explicit per-call values.
    const enrichedPayload = {
      ...payload,
      userRole: payload.userRole ?? ClientLogsService.userRole,
      correlationId: payload.correlationId ?? ClientLogsService.getCorrelationId(),
    };

    try {
      await callApi(buildClientLogsConfig(enrichedPayload));
    } catch (err) {
      // Log error via the frontend logger with 'error' level
      ClientLogsService.logInternal({
        level: "error",
        text: "Failed to send client log",
        value: { error: err instanceof Error ? err.message : String(err) },
        error: err instanceof Error ? err : undefined,
        location: "ClientLogsService.sendLog",
      });
    }
  }

  /**
   * Log a page navigation
   * @param logObj - Optional LogObject to trigger internal logging
   */
  static async logNavigation(
    userUuid: string,
    location: string,
    message?: string,
    logObj?: LogObject,
  ): Promise<void> {
    // Convert pathname to Title Case Page format
    const pathName = location.startsWith("/") ? location.slice(1) : location;

    const titleCased = pathName
      .split("/")
      .map((segment) =>
        segment
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      )
      .join("/");

    const locationDisplayName = titleCased ? `${titleCased} Page` : "Unknown Page";

    return ClientLogsService.sendLog(
      {
        userUuid,
        url: typeof window !== "undefined" ? window.location.href : "",
        location,
        eventType: "navigation",
        message: message || `Navigated to ${locationDisplayName}`,
      },
      logObj,
    );
  }

  /**
   * Log a click event
   * @param logObj - Optional LogObject to trigger internal logging
   */
  static async logClick(
    userUuid: string,
    location: string,
    elementId: string,
    label?: string,
    message?: string,
    logObj?: LogObject,
  ): Promise<void> {
    return ClientLogsService.sendLog(
      {
        userUuid,
        url: typeof window !== "undefined" ? window.location.href : "",
        location,
        eventType: "click",
        message: message || `User clicked ${label || elementId}`,
        elementId,
      },
      logObj,
    );
  }

  /**
   * Log a client-side error
   * @param classification - Optional ADR-03 failure taxonomy classification used to derive
   * the log level (WARN vs ERROR) and alertability. Defaults to Unexpected System Failure (ERROR).
   */
  static async logError(
    userUuid: string,
    location: string,
    errorMessage: string,
    errorObject?: Error,
    classification?: {
      errorType?: FailureType;
      dependencyName?: string;
      retryCount?: number;
      functionName?: string;
    },
  ): Promise<void> {
    const level = getLogLevelForFailureType(classification?.errorType);
    const logObj: LogObject = {
      level,
      text: errorMessage,
      location: location,
      error: errorObject ? errorObject : undefined,
    };
    return ClientLogsService.sendLog(
      {
        userUuid,
        url: typeof window !== "undefined" ? window.location.href : "",
        location,
        eventType: "error",
        message: errorMessage,
        error: errorObject?.stack || errorObject?.message,
        errorType: classification?.errorType,
        dependencyName: classification?.dependencyName,
        retryCount: classification?.retryCount,
        functionName: classification?.functionName,
      },
      logObj,
    );
  }

  /**
   * Log a component action (modal display, form submission, filter change, etc.)
   * Captures any significant UI/component state change in the application
   * @param logObj - Optional LogObject to trigger internal logging
   */
  static async logWebAction(
    userUuid: string,
    location: string,
    action: string,
    details?: Record<string, unknown>,
    logObj?: LogObject,
  ): Promise<void> {
    const detailsString = details ? ` | Details: ${JSON.stringify(details)}` : "";

    const logObjFinal: LogObject | undefined = logObj
      ? {
          level: logObj.level || "info",
          location: logObj.location || location,
          text: logObj.text || action,
          ...logObj,
        }
      : undefined;

    return ClientLogsService.sendLog(
      {
        userUuid,
        url: typeof window !== "undefined" ? window.location.href : "",
        location,
        eventType: "webAction",
        message: `${location} - ${action}${detailsString}`,
      },
      logObjFinal,
    );
  }

  /** Log that an assigned feature experience was actually rendered. */
  static async logFeatureExposure(
    userUuid: string,
    fields: Required<
      Pick<
        ClientLogPayload,
        | "bootstrapTransactionID"
        | "featureKey"
        | "variant"
        | "experience"
        | "route"
        | "eventTimestamp"
      >
    >,
  ): Promise<void> {
    return ClientLogsService.sendLog({
      userUuid,
      url: typeof window !== "undefined" ? window.location.href : "",
      location: fields.route,
      eventType: "feature_exposure_rendered",
      ...fields,
    });
  }

  /** Sends a structured, identity-free provider-availability outcome event. */
  static async logProviderAvailabilityOutcome(
    eventName: ProviderAvailabilityOutcomeEventName,
    correlationId: string,
    details: Record<string, string | number | boolean | null>,
  ): Promise<void> {
    return ClientLogsService.sendLog({
      userUuid: "system",
      url: typeof window !== "undefined" ? window.location.pathname : "",
      location: "ProviderList.availabilityOutcome",
      eventType: "webAction",
      message: eventName,
      eventName,
      correlationId,
      details,
    });
  }

  /**
   * Log a user-caused error (form validation, input errors, etc.)
   * Distinguishes user mistakes from system errors
   * @param userUuid - User UUID
   * @param location - Code location or page name
   * @param errorMessage - Error description
   * @param details - Optional details object (can include validation field info, etc.)
   * @param logObj - Optional LogObject to trigger internal logging
   */
  static async logUserError(
    userUuid: string,
    location: string,
    errorMessage: string,
    details?: Record<string, unknown>,
    logObj?: LogObject,
  ): Promise<void> {
    const detailsString = details ? ` | Details: ${JSON.stringify(details)}` : "";

    const logObjFinal: LogObject | undefined = logObj
      ? {
          level: logObj.level || "warn",
          location: logObj.location || location,
          text: logObj.text || errorMessage,
          ...logObj,
        }
      : undefined;

    return ClientLogsService.sendLog(
      {
        userUuid,
        url: typeof window !== "undefined" ? window.location.href : "",
        location,
        eventType: "userError",
        message: `${errorMessage}${detailsString}`,
      },
      logObjFinal,
    );
  }
}

export default ClientLogsService;
